import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiPost } from '../lib/api';
import { PageTitle, Card, Btn, Section, Kpi, Pill } from '../components/ui-kit';

export default function ContractDraft() {
  const { getToken } = useXyqarAuth();
  const nav = useNavigate();
  const [form, setForm] = React.useState({
    title: '',
    counterparty: '',
    mission: '',
    amount_eur: '',
    duration_months: '',
    signatories: '',
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  const signatoryList = form.signatories
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  const isReadyToDraft = Boolean(form.title.trim() && form.counterparty.trim() && signatoryList.length > 0);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!isReadyToDraft) {
      setErr('Title, counterparty, and at least one signatory are required.');
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const tok = await getToken().catch(() => '');
      const j = await apiPost<{ id: string }>('/api/v1/exec/contracts/draft', form, tok);
      if (j?.id) nav(`/contracts/${j.id}/review`);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const inp = 'w-full rounded-md bg-white/5 border border-white/10 px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-white/40';

  return (
    <div>
      <PageTitle kicker="Contracts" title="New draft" />

      <Section title="Draft control" hint="This page prepares a legally reviewable draft before review and signature are opened.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Title" value={form.title.trim() ? 'set' : 'missing'} accent={form.title.trim() ? 'green' : 'amber'} />
          <Kpi label="Counterparty" value={form.counterparty.trim() ? 'set' : 'missing'} accent={form.counterparty.trim() ? 'green' : 'amber'} />
          <Kpi label="Signatories" value={signatoryList.length} hint="comma-separated emails" accent={signatoryList.length > 0 ? 'green' : 'amber'} />
          <Kpi label="Draft state" value={isReadyToDraft ? 'ready' : 'incomplete'} accent={isReadyToDraft ? 'green' : 'amber'} />
        </div>
      </Section>

      <div className="grid gap-6 xl:grid-cols-[1.4fr,0.8fr]">
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Title">
              <input className={inp} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="MSA / SOW / mission addendum" />
            </Field>
            <Field label="Counterparty">
              <input className={inp} value={form.counterparty} onChange={(e) => set('counterparty', e.target.value)} placeholder="Client legal entity" />
            </Field>
            <Field label="Mission ID (or short description)">
              <input className={inp} value={form.mission} onChange={(e) => set('mission', e.target.value)} placeholder="Mission ref or legal scope" />
            </Field>
            <Field label="Amount (EUR)">
              <input type="number" className={inp} value={form.amount_eur} onChange={(e) => set('amount_eur', e.target.value)} />
            </Field>
            <Field label="Duration (months)">
              <input type="number" className={inp} value={form.duration_months} onChange={(e) => set('duration_months', e.target.value)} />
            </Field>
            <Field label="Signatories (comma-separated emails)">
              <input className={inp} value={form.signatories} onChange={(e) => set('signatories', e.target.value)} placeholder="legal@client.com, signer@xyqar.com" />
            </Field>
          </div>

          {err && <p className="text-red-300 text-sm mt-4">{err}</p>}

          <div className="flex justify-end mt-6">
            <Btn primary onClick={submit} disabled={submitting || !isReadyToDraft}>
              {submitting ? 'Creating…' : 'Generate draft →'}
            </Btn>
          </div>
        </Card>

        <div className="space-y-6">
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Review checklist</p>
            <div className="mt-4 space-y-3 text-sm text-white/80">
              <p>Counterparty naming should match the legal entity that will actually sign.</p>
              <p>Mission scope should be specific enough for review, even if the formal PDF is generated later.</p>
              <p>Signatory emails should include every party expected in the review and signature flow.</p>
            </div>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Draft snapshot</p>
            <div className="mt-4 space-y-3 text-sm text-white/80">
              <div className="flex items-center justify-between"><span className="text-white/50">Scope</span><span>{form.mission.trim() || 'Not specified yet'}</span></div>
              <div className="flex items-center justify-between"><span className="text-white/50">Amount</span><span>{form.amount_eur ? `${form.amount_eur} EUR` : 'Not set'}</span></div>
              <div className="flex items-center justify-between"><span className="text-white/50">Duration</span><span>{form.duration_months ? `${form.duration_months} months` : 'Not set'}</span></div>
              <div className="pt-2 flex flex-wrap gap-2">
                {signatoryList.length === 0 && <Pill>no signatory yet</Pill>}
                {signatoryList.map((signatory) => <Pill key={signatory}>{signatory}</Pill>)}
              </div>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-xs uppercase tracking-wider text-white/60">{label}</span>
      {children}
    </label>
  );
}
